import { useEffect, useState } from 'react';

/**
 * L'état du réseau, fondé sur l'ÉCHEC OBSERVÉ — décision #24, appliquée par #86.
 *
 * ## Pourquoi pas `navigator.onLine`
 *
 * Il **ment précisément dans le cas hôtelier**. Connecté au Wi-Fi de
 * l'établissement sans lien montant — sous-sol, ascenseur, portail captif — il
 * renvoie `true`. L'`onlineManager` de react-query en hérite, donc sa
 * pause/reprise ne se déclencherait pas dans le scénario dominant.
 *
 * Il est donc écouté **à sens unique, celui où il ne ment pas** :
 *
 * - il dit `false` → on le croit, et on affiche immédiatement ;
 * - il dit `true` → on ne le croit pas, et on attend un échec réel.
 *
 * C'est le délai d'abandon de 15 s (`axios-client.ts`) qui rend cet échec
 * observable assez vite pour servir de signal. Sans lui, une requête suspendue ne
 * renoncerait jamais et le bandeau n'apparaîtrait pas.
 *
 * ## Ce que ce hook ne fait pas
 *
 * Il ne déclenche aucun réessai. #24 écarte la file d'envoi et le réessai
 * automatique — pas pour une raison technique, mais parce qu'en hôtellerie
 * **l'heure fait le sens** : « j'arrive dans 5 minutes » délivré quatre heures
 * plus tard est devenu faux.
 */

/** L'événement qu'`axios-client` émet sur un échec réseau observé. */
export const ECHEC_RESEAU_EVENT = 'tf-echec-reseau';

/** Et celui d'une requête qui aboutit — le seul signal de retour fiable. */
export const SUCCES_RESEAU_EVENT = 'tf-succes-reseau';

export function useReseau(): { horsLigne: boolean } {
    const [horsLigne, setHorsLigne] = useState(
        () => typeof navigator !== 'undefined' && navigator.onLine === false,
    );

    useEffect(() => {
        const surEchec = () => setHorsLigne(true);
        const surSucces = () => setHorsLigne(false);

        // `offline` est cru sur parole : quand le navigateur dit qu'il n'y a plus
        // de lien, il ne se trompe pas — c'est l'inverse qui ment.
        const surPerte = () => setHorsLigne(true);

        window.addEventListener(ECHEC_RESEAU_EVENT, surEchec);
        window.addEventListener(SUCCES_RESEAU_EVENT, surSucces);
        window.addEventListener('offline', surPerte);

        // ⚠️ `online` n'est PAS écouté : il se déclenche dès que le Wi-Fi est
        // capté, sans rien dire du lien montant. Le retour se constate sur une
        // requête qui aboutit, jamais sur une promesse du navigateur.
        return () => {
            window.removeEventListener(ECHEC_RESEAU_EVENT, surEchec);
            window.removeEventListener(SUCCES_RESEAU_EVENT, surSucces);
            window.removeEventListener('offline', surPerte);
        };
    }, []);

    return { horsLigne };
}
